'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useUser } from '@medizzconnect/auth';
import { cn } from '@medizzconnect/shared/utils';
import type { StripePlanRow } from '@medizzconnect/db';
import { HeroBanner } from '@/components/layout/hero-banner';
import { useAutoplayUnlock, robustUnmute } from '@/lib/use-autoplay-unlock';
import { HelpContactDialog } from '@/components/shared/help-contact-dialog';

function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/** Plans nach plan_key gruppieren (monthly + yearly als Paar) */
interface PlanGroup {
  plan_key: string;
  plan_name: string;
  monthly: StripePlanRow | null;
  yearly: StripePlanRow | null;
  features: string[];
  is_recommended: boolean;
}

interface PlanVideo {
  id: number;
  plan_key: string;
  video_url: string;
  thumbnail_url: string | null;
  processed_video_url: string | null;
  processed_thumbnail_url: string | null;
  processed_status: string | null;
  title: string | null;
  description: string | null;
  duration_seconds: number | null;
}

/** Inline Video-Player für Plan-Videos – Hover-to-Play wie Homepage */
function PlanVideoPlayer({ video }: { video: PlanVideo }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAudioUnlocked = useAutoplayUnlock();

  // Sound-Allowed Listener: Unmute aktuell spielendes Video nach Consent
  useEffect(() => {
    const onSoundAllowed = () => {
      const vid = videoRef.current;
      if (vid && !vid.paused && vid.muted) {
        robustUnmute(vid);
      }
    };
    document.addEventListener('sound-allowed', onSoundAllowed);
    return () => document.removeEventListener('sound-allowed', onSoundAllowed);
  }, []);

  const handleMouseEnter = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
      const vid = videoRef.current;
      if (vid) {
        vid.currentTime = 0;
        vid.muted = true;
        vid.play().then(() => {
          if (isAudioUnlocked) {
            robustUnmute(vid);
          } else {
            document.dispatchEvent(new Event('request-audio-consent'));
          }
        }).catch(() => {});
      }
    }, 50);
  }, [isAudioUnlocked]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovering(false);
    const vid = videoRef.current;
    if (vid) {
      vid.muted = true;
      vid.pause();
      vid.currentTime = 0;
    }
  }, []);

  if (hasError || !video.video_url) {
    return null;
  }

  // Optimiertes Video bevorzugen, Original als Fallback
  const videoSrc = video.processed_video_url || video.video_url;
  const thumbnailSrc = video.processed_thumbnail_url || video.thumbnail_url || '';

  return (
    <div
      className="mb-4 rounded-xl overflow-hidden relative group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative aspect-[5/4]">
        {/* Thumbnail */}
        {thumbnailSrc && (
          <img
            src={thumbnailSrc}
            alt={video.title || 'Plan Video'}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              isHovering && isVideoReady ? 'opacity-0' : 'opacity-100'
            }`}
          />
        )}

        {/* Video (Hover-to-Play) */}
        <video
          ref={videoRef}
          src={videoSrc}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isHovering && isVideoReady ? 'opacity-100' : 'opacity-0'
          }`}
          muted
          loop
          playsInline
          preload="none"
          onCanPlay={() => setIsVideoReady(true)}
          onError={() => setHasError(true)}
          onVolumeChange={(e) => {
            const vid = e.currentTarget;
            if (!vid.muted && vid.paused && !isAudioUnlocked) {
              vid.muted = true;
              vid.play().catch(() => {});
            }
          }}
        />



        {/* Dauer-Badge */}
        {video.duration_seconds && !isHovering && (
          <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
            {Math.floor(video.duration_seconds / 60)}:{(video.duration_seconds % 60).toString().padStart(2, '0')}
          </span>
        )}
      </div>
      {video.title && (
        <p
          className="px-3 truncate text-center"
          style={{
            marginTop: '4px',
            paddingBottom: '6px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: '#2c7a7b',
          }}
        >
          {video.title}
        </p>
      )}
    </div>
  );
}

function groupPlans(plans: StripePlanRow[]): PlanGroup[] {
  const map = new Map<string, PlanGroup>();
  for (const p of plans) {
    if (!map.has(p.plan_key)) {
      map.set(p.plan_key, {
        plan_key: p.plan_key,
        plan_name: p.plan_name,
        monthly: null,
        yearly: null,
        features: [],
        is_recommended: false,
      });
    }
    const group = map.get(p.plan_key)!;
    if (p.billing_cycle === 'monthly') group.monthly = p;
    else group.yearly = p;
    if (p.is_recommended) group.is_recommended = true;
    if (group.features.length === 0 && p.features) {
      group.features = p.features as unknown as string[];
    }
  }
  return Array.from(map.values());
}

export function PricingPage() {
  const t = useTranslations('subscription');
  const locale = useLocale();
  const { user, isLoaded, isSignedIn } = useUser();
  const isLoggedIn = isLoaded && !!isSignedIn;
  const [isYearly, setIsYearly] = useState(false);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [plans, setPlans] = useState<StripePlanRow[]>([]);
  const [addons, setAddons] = useState<StripePlanRow[]>([]);
  const [planVideos, setPlanVideos] = useState<PlanVideo[]>([]);
  const [erklaervideoUrl, setErklaervideoUrl] = useState<string | null>(null);
  const [erklaervideoProcessedUrl, setErklaervideoProcessedUrl] = useState<string | null>(null);
  const [erklaervideoThumbnailUrl, setErklaervideoThumbnailUrl] = useState<string | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Plans + Addons aus DB laden
  useEffect(() => {
    async function loadPlans() {
      setLoadingPlans(true);
      try {
        const res = await fetch(`/api/onboarding/plans?locale=${locale}`);
        const data = await res.json();
        setPlans(data.plans || []);
        setAddons(data.addons || []);
        setPlanVideos(data.planVideos || []);
        setErklaervideoUrl(data.erklaervideoUrl ?? null);
        setErklaervideoProcessedUrl(data.erklaervideoProcessedUrl ?? null);
        setErklaervideoThumbnailUrl(data.erklaervideoThumbnailUrl ?? null);
      } catch {
        // Fehler still behandeln
      } finally {
        setLoadingPlans(false);
      }
    }
    loadPlans();
  }, [locale]);

  const planGroups = groupPlans(plans);

  const handleSubscribe = useCallback(async (group: PlanGroup) => {
    if (group.plan_key === 'BASIS') return;

    if (!isLoggedIn || !user) {
      window.location.href = '/onboarding';
      return;
    }

    setIsLoading(group.plan_key);
    try {
      const plan = isYearly ? group.yearly : group.monthly;
      if (!plan?.price_id) {
        window.location.href = '/contact?plan=' + group.plan_key;
        return;
      }

      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: plan.price_id,
          userId: user.id,
          email: user.emailAddresses?.[0]?.emailAddress,
          locale,
        }),
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      // Silent fail
    } finally {
      setIsLoading(null);
    }
  }, [isLoggedIn, user, isYearly, locale]);

  // Yearly-Ersparnis berechnen
  function savingsPercent(group: PlanGroup): number {
    if (!group.monthly || !group.yearly || group.monthly.price_amount === 0) return 0;
    const yearlyMonthly = group.yearly.price_amount / 12;
    return Math.round((1 - yearlyMonthly / group.monthly.price_amount) * 100);
  }

  return (
    <>
      <HeroBanner className="mb-8" />
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {/* 24/7 Vertrauen */}
        <div className="flex flex-col items-center text-center rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
          <div className="mb-4 text-gray-800">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('trustTitle')}</h3>
          <p className="text-sm text-gray-500 leading-relaxed">{t('trustText')}</p>
        </div>
        {/* Sofort starten */}
        <div className="flex flex-col items-center text-center rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
          <div className="mb-4 text-gray-800">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
              <path d="M13 3h-2v10h2V3zm4.83 2.17l-1.42 1.42C17.99 7.86 19 9.81 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.19 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('startTitle')}</h3>
          <p className="text-sm text-gray-500 leading-relaxed">{t('startText')}</p>
        </div>
        {/* Persönliche Reichweite */}
        <div className="flex flex-col items-center text-center rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
          <div className="mb-4 text-gray-800">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('reachTitle')}</h3>
          <p className="text-sm text-gray-500 leading-relaxed">{t('reachText')}</p>
        </div>
      </div>

      {/* Pricing Header */}
      <div className="text-center mb-12" style={{ padding: '60px 0' }}>
        <span
          className="inline-flex items-center gap-2 text-sm font-semibold text-white mb-6"
          style={{
            background: 'linear-gradient(135deg, #38b2ac, #85e0d3)',
            padding: '12px 24px',
            borderRadius: '30px',
            boxShadow: '0 4px 20px rgba(56, 178, 172, 0.3)',
            display: 'inline-flex',
          }}
        >
          {t('pricingBadge')}
        </span>
        <h1
          className="font-bold mb-6"
          style={{
            fontSize: 'clamp(36px, 5vw, 56px)',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #2c7a7b, #38b2ac)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t('pricingHeadline')}
        </h1>
        <p className="text-xl text-gray-500 max-w-xl mx-auto" style={{ lineHeight: 1.6 }}>
          {t('pricingSubline')}
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4 mb-12">
        <span className={cn('text-sm font-medium', !isYearly ? 'text-gray-900' : 'text-gray-400')}>
          {t('monthly')}
        </span>
        <button
          onClick={() => setIsYearly(!isYearly)}
          className={cn(
            'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
            isYearly ? 'bg-primary' : 'bg-gray-200'
          )}
          role="switch"
          aria-checked={isYearly}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition-transform',
              isYearly ? 'translate-x-5' : 'translate-x-0'
            )}
          />
        </button>
        <span className={cn('text-sm font-medium', isYearly ? 'text-gray-900' : 'text-gray-400')}>
          {t('yearly')}
          <span className="ml-1 text-xs text-green-600 font-semibold">
            {t('savePercent', { percent: '10' })}
          </span>
        </span>
      </div>

      {/* Loading State */}
      {loadingPlans ? (
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        /* Pricing Cards */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {planGroups.map((group, index) => {
            const plan = isYearly ? group.yearly : group.monthly;
            const price = plan?.price_amount ?? 0;
            const perLabel = isYearly ? t('perYear') : t('perMonth');
            const savings = savingsPercent(group);
            const cardGradients = [
              'linear-gradient(135deg, #38b2ac, #85e0d3)',
              'linear-gradient(135deg, #38b2ac, #319795)',
              'linear-gradient(135deg, #2c7a7b, #246466)',
            ];
            const headerGradient = cardGradients[index % cardGradients.length];

            return (
              <div
                key={group.plan_key}
                className="relative flex flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{
                  borderRadius: '10px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #f1f5f9',
                }}
              >
                {/* Gradient Header wie V1 */}
                <div
                  className="text-center text-white relative overflow-hidden"
                  style={{
                    background: headerGradient,
                    padding: '30px',
                  }}
                >
            
                  <h3
                    className="text-white font-bold"
                    style={{ fontSize: '24px', letterSpacing: '1px' }}
                  >
                    {group.plan_name}
                  </h3>
                </div>

                {/* Content Area */}
                <div className="flex flex-col flex-1 bg-white" style={{ padding: '40px 30px 30px', borderRadius: '0 0 20px 20px' }}>
                  <div className="text-center mb-6" style={{ paddingBottom: '20px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '48px', fontWeight: 700, color: '#2c7a7b' }}>
                      {price === 0 ? t('free') : `€${formatPrice(price)}`}
                    </span>
                    {price > 0 && (
                      <span className="text-base text-gray-500 ml-1">{perLabel}</span>
                    )}
                    {isYearly && savings > 0 && (
                      <span className="ml-2 text-xs text-green-600 font-semibold">
                        -{savings}%
                      </span>
                    )}
                  </div>

                  {/* Plan-Video */}
                  {(() => {
                    const video = planVideos.find(v => v.plan_key === group.plan_key);
                    return video ? (
                      <div className="rounded-xl overflow-hidden mb-5" style={{ background: 'linear-gradient(135deg, rgba(56, 178, 172, 0.1), rgba(79, 209, 199, 0.15))', padding: '20px' }}>
                        <PlanVideoPlayer video={video} />
                      </div>
                    ) : null;
                  })()}

                  <ul className="flex-1" style={{ listStyle: 'none', padding: 0, margin: '20px 0' }}>
                    {group.features.map((feature, i) => (
                      <li
                        key={i}
                        className="text-sm text-gray-600 relative"
                        style={{ padding: '12px 0 12px 30px', borderBottom: '1px solid #f1f5f9' }}
                      >
                        <span className="absolute left-0 text-primary font-semibold">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(group)}
                    disabled={isLoading === group.plan_key || group.plan_key === 'BASIS'}
                    className={cn(
                      'w-full text-white font-semibold cursor-pointer transition-all duration-300',
                      group.plan_key === 'BASIS' && 'opacity-60 cursor-default',
                      isLoading === group.plan_key && 'opacity-50 cursor-not-allowed'
                    )}
                    style={{
                      background: group.plan_key === 'BASIS'
                        ? '#e2e8f0'
                        : 'linear-gradient(135deg, #38b2ac, #85e0d3)',
                      color: group.plan_key === 'BASIS' ? '#a0aec0' : 'white',
                      border: 'none',
                      padding: '16px 32px',
                      borderRadius: '12px',
                      fontSize: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      boxShadow: group.plan_key === 'BASIS'
                        ? 'none'
                        : '0 4px 20px rgba(56, 178, 172, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      if (group.plan_key !== 'BASIS') {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(56, 178, 172, 0.4)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #2c7a7b, #38b2ac)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (group.plan_key !== 'BASIS') {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(56, 178, 172, 0.3)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #38b2ac, #85e0d3)';
                      }
                    }}
                  >
                    {group.plan_key === 'BASIS'
                      ? t('freePlan')
                      : isLoading === group.plan_key
                        ? '...'
                        : isLoggedIn
                          ? t('upgrade')
                          : t('bookNow')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add-ons Section */}
      {addons.length > 0 && (
        <div className="mt-20" style={{ background: 'linear-gradient(135deg, #f8fafc, #ffffff)', borderRadius: '30px', padding: '80px 40px' }}>
          <div className="text-center mb-12">
            <h2
              className="text-2xl sm:text-3xl font-bold mb-6"
              style={{
                background: 'linear-gradient(135deg, #2c7a7b, #38b2ac)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: '36px',
              }}
            >
              {t('addonsTitle')}
            </h2>
            <p className="text-lg text-gray-500 max-w-xl mx-auto">
              {t('addonsSubtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {addons.map((addon) => (
              <div
                key={addon.id}
                className="flex flex-col items-center rounded-2xl border border-gray-200 p-8 hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.66-5.66a8 8 0 1113.66 0l-5.66 5.66a2 2 0 01-2.83 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 12a9.75 9.75 0 11-19.5 0 9.75 9.75 0 0119.5 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {addon.plan_name}
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  {t('addonLabel')}: {addon.plan_name}
                </p>
                <span className="text-primary font-semibold">
                  +€{formatPrice(addon.price_amount)}{t('perMonth')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box: Nettopreise + Laufzeit */}
      <div className="mt-12 max-w-2xl mx-auto">
        <div className="bg-primary/5 border-l-4 border-primary rounded-r-2xl p-6">
          <div className="flex gap-3">
            <span className="text-2xl">💡</span>
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                <strong>{t('netPriceLabel')}:</strong> {t('netPriceText')}
              </p>
              <p>
                <strong>{t('termLabel')}:</strong> {t('termText')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Text */}
      <div className="mt-16 max-w-3xl mx-auto text-center text-gray-600 text-sm leading-relaxed space-y-4">
        <p>{t('testimonialText1')}</p>
        <p>{t('testimonialText2')}</p>
      </div>

      {/* Erklärvideo Section */}
      <div className="mt-20 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
          {t('videoTitle')}
        </h2>
        <p className="text-base text-gray-500 max-w-2xl mx-auto mb-8">
          {t('videoSubtitle')}
        </p>
        <div className="relative max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-xl">
          <video
            className="w-full"
            controls
            preload="metadata"
            playsInline
            poster={erklaervideoThumbnailUrl || '/images/default-video-thumbnail.jpg'}
            src={erklaervideoProcessedUrl || erklaervideoUrl || `${process.env.NEXT_PUBLIC_ERKLAERVIDEO_BASE_URL || 'https://ztptdlytsupvpgadvwdt.supabase.co/storage/v1/object/public/system-media/erklaer-videos'}/2025_05_erklaerfilm-${locale}.mp4`}
          >
          </video>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="mt-12 text-center">
        <HelpContactDialog>
          <button
            className="inline-block text-white font-semibold transition-all duration-300 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #27b4ad, #0d7a81)',
              padding: '14px 28px',
              borderRadius: '8px',
              fontSize: '16px',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.25)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t('contactButton')}
          </button>
        </HelpContactDialog>
      </div>
    </div>
    </>
  );
}
